import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { usePlatformStore, useMarketingMode, useDialog } from "@platform/store";
import { Loader } from "@themes/pollinator/ui/Loader";
import { marketingEventBus } from "@platform/events/marketing.events";
import {
  type Invitation,
  useClaimInvitation,
  useCheckInvitationClaimStatus,
} from "@core/features/invitation";
import { APIError } from "@core/services/api/apiRequest";
import { validateOrgInvitation } from "@platform/utils/validateOrgInvitation";
import { MarketingHero } from "./MarketingHero";

/**
 * Container component for the marketing hero section.
 * Handles invitation validation from URL shortcode and renders appropriate hero variant.
 */

export function MarketingHeroContainer() {
  const navigate = useNavigate();
  const { mode } = useMarketingMode();
  const user = usePlatformStore((s) => s.user);
  const { openDialog } = useDialog();

  const setInvitationStore = usePlatformStore((s) => s.setInvitation);

  const [isValidatingCode, setIsValidatingCode] = useState<boolean>(false);

  const [invitation, setInvitation] = useState<Invitation | null>(null);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [errorInvitation, setErrorInvitation] = useState<Invitation | null>(
    null
  );

  const { shortcode } = useParams();

  // Check if user has already claimed this invitation type for the program
  const { data: claimStatus } = useCheckInvitationClaimStatus({
    user,
    invitation,
  });
  const isEnrolled = claimStatus?.has_claimed_type === true;

  // Claim mutation for one-click claiming
  const { mutate: claimInvitation, isPending: isClaimLoading } =
    useClaimInvitation();

  const handleClaimClick = () => {
    if (!invitation) return;

    // If quiz configured, navigate to quiz (quiz handles claiming after completion)
    const quizId = invitation.display_options?.quiz_id;
    if (quizId) {
      navigate(`/invitation/${invitation.shortcode}/fit`);
    } else {
      claimInvitation({
        shortcode: invitation.shortcode,
        claimedVia: "direct_link",
      });
    }
  };

  useEffect(() => {
    if (!shortcode) return;

    setIsValidatingCode(true);
  }, [shortcode]);

  useEffect(() => {
    if (!shortcode) return;

    validateOrgInvitation({ shortcode, type: "shortcode" })
      .then((invitation: Invitation) => {
        setInvitation(invitation);

        // emit event
        marketingEventBus.emitEvent("marketing:invitation:direct:viewed", {
          invitation,
        });

        setInvitationStore(invitation, "direct_link");

        setIsValidatingCode(false);
      })
      .catch((error: Error) => {
        const message = error.message || "";
        const isUnknownCode =
          message === "Invalid invitation code" ||
          message === "Template invitations cannot be claimed directly";

        if (isUnknownCode) {
          navigate("/", { replace: true });
        } else {
          setInvitationError(message);
          if (error instanceof APIError && error.data?.invitation) {
            setErrorInvitation(error.data.invitation);
          }
        }

        setIsValidatingCode(false);
      });
  }, [shortcode, setInvitationStore]);

  const handleJoinClick = () => {
    if (!invitation) return;

    // emit event
    marketingEventBus.emitEvent("marketing:invitation:direct:started", {
      invitation,
    });

    // navigate to the join page
    navigate(`/invitation/${invitation.shortcode}/join`);
  };

  if (isValidatingCode) return <Loader />;

  return (
    <MarketingHero
      onDashboardClick={() => navigate("/dashboard")}
      mode={mode}
      isAuthenticated={!!user}
      invitation={invitation}
      onJoinClick={handleJoinClick}
      isEnrolled={isEnrolled}
      onClaimClick={handleClaimClick}
      isClaimLoading={isClaimLoading}
      invitationError={invitationError}
      errorInvitation={errorInvitation}
      shortcode={shortcode}
      onGoBackClick={() => navigate("/")}
      onNewsletterClick={() => openDialog("newsletter")}
    />
  );
}

export default MarketingHeroContainer;

